import { Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import CancelDialogIcon from "@entrilia/shared/icons/CancelDialogIcon";
import CompleteDialogIcon from "@entrilia/shared/icons/CompleteDialogIcon";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { WorkflowDefinition, WorkflowTemplateVersionStatus } from "../../../../api/types/workflowTypes";

interface Props {
  workflowDefinition: WorkflowDefinition;
  targetStatus: WorkflowTemplateVersionStatus;
  onClose: () => void;
  onUpdated: (updatedDefinition: WorkflowDefinition) => void;
}

const updateWorkflowDefinitionStatus = withErrorHandling(adminApi.updateWorkflowDefinitionStatus);

const UpdateWorkflowDefinitionStatusDialog = ({ workflowDefinition, targetStatus, onClose, onUpdated }: Props) => {
  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [updating, setUpdating] = useState(false);

  const isActivating = targetStatus === "Active";

  const handleUpdate = async () => {
    setUpdating(true);
    const [resp, error] = await updateWorkflowDefinitionStatus(workflowDefinition.workflowType, {
      status: targetStatus,
    });
    setUpdating(false);

    if (error) {
      logError(error, "[UpdateWorkflowDefinitionStatusDialog] updateWorkflowDefinitionStatus");
      sendNotificationError(error.message);
      return;
    }

    sendNotification(`Workflow ${isActivating ? "activated" : "deactivated"} successfully`);
    onUpdated(resp);
    onClose();
  };

  return (
    <SmallDialog open onClose={onClose}>
      <DialogTitle>Confirm</DialogTitle>
      <DialogCloseButton onClick={onClose} />
      <DialogContent>
        <Stack spacing={2} sx={{ alignItems: "center" }}>
          {isActivating ? (
            <CompleteDialogIcon sx={{ width: 80, height: 80 }} />
          ) : (
            <CancelDialogIcon sx={{ width: 80, height: 80 }} />
          )}

          <Typography variant="h6" sx={{ textAlign: "center" }}>
            {isActivating ? "Activate" : "Deactivate"} workflow {workflowDefinition.name}?
          </Typography>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={onClose}>
        <Button
          color={isActivating ? "primary" : "error"}
          variant={isActivating ? "contained" : "outlined"}
          loading={updating}
          onClick={handleUpdate}
        >
          {isActivating ? "Activate" : "Deactivate"}
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default UpdateWorkflowDefinitionStatusDialog;
